import { max } from 'date-fns'
import { useEffect, useMemo } from 'react'
import { DeepPartial } from 'react-hook-form'

import { InvoiceLineType } from '@features/invoices/enums/invoiceLineType'
import { InvoiceForm } from '@features/invoices/types/invoiceForm'
import { InvoiceFormRowLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormRowLine'

import { useInvoiceEditorForm } from '../contexts/invoiceEditorFormContext'
import { getIsInvoiceLinesChange } from '../utils/invoiceEditorForm/getIsInvoiceLinesChange'
import { useInvoiceEditorDocumentType } from './useInvoiceEditorDocumentType'
import { useInvoiceVisibleColumns } from './useInvoiceVisibleColumns'

export const useUpdateDateOfSaleOrService = () => {
  const { invoiceEditorForm } = useInvoiceEditorForm()
  const { visibleColumns } = useInvoiceVisibleColumns()
  const { isRecurringInvoice } = useInvoiceEditorDocumentType()

  const { formState, getValues, setValue, watch } = invoiceEditorForm

  const isDateColumnVisible = visibleColumns.includes('date')

  const onInvoiceLinesChange = useMemo(
    () =>
      (_formValues: DeepPartial<InvoiceForm>, { name: changedPath }: { name?: string }) => {
        // formState.isDirty is false after reset() — blocks programmatic updates during draft loading,
        // preserving the saved dateOfSaleOrService. Auto-sync resumes once the user makes their first change.
        if (!isDateColumnVisible || !getIsInvoiceLinesChange(changedPath, 'date') || !formState.isDirty) {
          return
        }

        const invoiceLines = getValues('invoiceLines')
        const rowLineDates = invoiceLines
          .filter((line): line is InvoiceFormRowLine => line.type === InvoiceLineType.Row)
          .map((line) => line.date)
          .filter(Boolean)

        if (rowLineDates.length > 0) {
          setValue('dateOfSaleOrService', max(rowLineDates))
        }
      },
    [formState, getValues, isDateColumnVisible, setValue],
  )

  useEffect(() => {
    if (!isRecurringInvoice) {
      const { unsubscribe } = watch(onInvoiceLinesChange)

      return unsubscribe
    }
  }, [watch, onInvoiceLinesChange, isRecurringInvoice])
}
